/**
 * RitualCard Component
 * Displays a ritual with full-bleed cover image, user avatar, and metadata
 * Card with bottom gradient overlay for text readability
 */

import { useState } from "react"
import { useNavigate } from "react-router-dom"
import { BookmarkFilledIcon, BookmarkIcon } from "@/components/icons"
import { Avatar } from "@/components/ui/avatar"
import { Card } from "@/components/ui/card"
import { LoginModal } from "@/features/auth/components/LoginModal"
import { useAuthStore } from "@/features/auth/store/authStore"

export interface RitualCardProps {
  id: string
  slug?: string
  image?: string
  userAvatar?: string
  username: string
  userHandle: string
  title: string
  tags: string[]
  duration?: string
  contextualTag?: string
  isBookmarked?: boolean
  onBookmarkClick?: () => void
  userId?: string
  price?: number
  participationFee?: "Free" | "Paid" | "Donation-based"
  organizersCount?: number
  ritualTs?: string | null
  city?: string | null
  state?: string | null
}

export function RitualCard({
  id,
  image,
  userAvatar,
  username,
  title,
  tags,
  duration,
  isBookmarked = false,
  onBookmarkClick,
  userId,
  price,
  participationFee,
  organizersCount,
  ritualTs,
  city,
  state,
}: RitualCardProps) {
  const navigate = useNavigate()
  const user = useAuthStore(state => state.user)
  const [showLoginModal, setShowLoginModal] = useState(false)
  const avatarFallback = username?.[0] || "?"

  // Determine price badge content and styling
  const priceBadge =
    participationFee === "Free"
      ? { content: "Free", bgClass: "bg-brand-olive" }
      : participationFee === "Donation-based"
        ? { content: "Donation-based", bgClass: "bg-brand-terracotta" }
        : participationFee === "Paid" && price !== undefined
          ? { content: `$${price}`, bgClass: "bg-brand-terracotta" }
          : null

  // Build metadata parts: duration · date/time · @city, state
  const metaParts: string[] = []
  if (duration) metaParts.push(duration)
  if (ritualTs) {
    try {
      const d = new Date(ritualTs)
      const dateStr = d.toLocaleDateString("en-US", { month: "short", day: "numeric" })
      const timeStr = d.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      })
      metaParts.push(`${dateStr}, ${timeStr}`)
    } catch {
      // ignore invalid dates
    }
  }
  if (city) {
    metaParts.push(`@${city}${state ? `, ${state}` : ""}`)
  }
  const metaLine = metaParts.join("  ")

  return (
    <>
      <Card
        className="overflow-hidden h-full w-full aspect-16/11 border border-[#F2F0EB26] !rounded-tl-[40px] !rounded-br-[40px] md:rounded-tl-[40px] md:rounded-br-[40px] bg-transparent p-0 cursor-pointer rounded-none"
        onClick={() => navigate(`/rituals/${id}`)}
      >
        <div className="relative h-full w-full overflow-hidden rounded-tl-[20px] rounded-br-[20px] md:rounded-tl-[40px] md:rounded-br-[40px]">
          {/* Cover Image */}
          {image ? (
            <img src={image} alt={title} className="absolute inset-0 h-full w-full object-cover" />
          ) : (
            <div className="absolute inset-0 bg-card" />
          )}

          {/*gradient mask */}
          <div
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(to bottom, rgba(0,0,0,0.05) 0%, rgba(0,0,0,0.12) 25%, rgba(0,0,0,0.35) 55%, rgba(0,0,0,0.7) 80%, rgba(0,0,0,1) 100%)",
            }}
          />

          {/* Top Section - User Info and Bookmark (positioned absolutely) */}
          <div className="absolute top-5 left-5 right-5 z-10 flex items-start justify-between">
            {/* User Profile */}
            <div className="flex items-center gap-2.5">
              <button
                type="button"
                onClick={e => {
                  e.stopPropagation()
                  if (userId) navigate(`/profile/${userId}`)
                }}
                className="cursor-pointer hover:scale-105 transition-transform"
                aria-label={`View ${username}'s profile`}
              >
                <Avatar
                  src={userAvatar}
                  fallback={avatarFallback}
                  size="lg"
                  className="w-[30px] h-[30px]"
                />
              </button>
              <div className="flex items-center gap-2">
                <span className="text-[13px] font-semibold text-primary opacity-90 leading-normal [text-shadow:0_3px_10px_rgba(2,2,2,0.95)]">
                  {username}
                </span>
                {organizersCount !== undefined && organizersCount > 1 && (
                  <span className="text-xs text-primary/70">+ {organizersCount}</span>
                )}
              </div>
            </div>

            {/* Bookmark Button */}
            {onBookmarkClick && (
              <button
                type="button"
                onClick={e => {
                  e.stopPropagation()
                  if (!user) {
                    setShowLoginModal(true)
                    return
                  }
                  onBookmarkClick()
                }}
                className="shrink-0 cursor-pointer rounded-[10px] border-gradient-glass p-2.75 transition-colors"
                aria-label={isBookmarked ? "Remove bookmark" : "Add bookmark"}
              >
                {isBookmarked ? (
                  <BookmarkFilledIcon className="w-[22px] h-[22px] fill-white text-white" />
                ) : (
                  <BookmarkIcon className="w-[22px] h-[22px] text-primary" />
                )}
              </button>
            )}
          </div>

          {/* Bottom Info Overlay */}
          <div className="absolute bottom-10 md:bottom-0 left-0 right-0 h-[84px] sm:h-29 z-10">
            {/* Content */}
            <div className="relative z-20 p-4 md:px-[30px] md:py-[20px] h-full">
              {/* Title */}
              <div className="absolute left-4 md:left-5 top-4 md:top-[30px] w-full">
                <h3 className="truncate text-[14px] md:text-[18px] font-semibold leading-[22px] text-primary max-w-[calc(100%-40px)] drop-shadow-[0_1px_3px_rgba(0,0,0,0.8)]">
                  {title}
                </h3>
              </div>

              {/* Price Badge, Tags, and Metadata */}
              <div className="absolute left-4 md:left-5 top-[48px] md:top-[62px] flex items-center flex-wrap gap-1.5 md:gap-2.5">
                {priceBadge && (
                  <span
                    className={`${priceBadge.bgClass} text-primary text-[13px] rounded-[5px] px-1.5 py-0.5 leading-5`}
                  >
                    {priceBadge.content}
                  </span>
                )}

                {tags.map((tag, index) => (
                  <span key={index} className="text-xs md:text-[13px] text-primary leading-5">
                    {tag}
                  </span>
                ))}

                {metaLine && (
                  <span className="text-xs md:text-[13px] text-primary leading-5 w-full sm:w-auto">
                    {metaLine}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </Card>
      <LoginModal open={showLoginModal} onOpenChange={setShowLoginModal} />
    </>
  )
}
